import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Users, Search, CheckCircle, XCircle, Eye, EyeOff, ChevronDown, ChevronUp,
  Mail, Phone, MapPin, Calendar, Star, Briefcase, Camera, IdCard, Crown, ExternalLink, MapPinned, AlertCircle, Trash2, Loader2, Key, ShieldOff, ShieldCheck,
  MessageSquare, Upload, Send, History
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const { getAuthHeader, userType } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docType, setDocType] = useState('');
  const [ratingUser, setRatingUser] = useState(null);
  const [pendingRating, setPendingRating] = useState(0);
  const [deletingUser, setDeletingUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordText, setShowPasswordText] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Check if current user is a full admin (can see PayPal emails)
  const isFullAdmin = userType === 'admin';
  // Admins and auditors can rate users
  const canRateUsers = userType === 'admin' || userType === 'auditor';
  // Admins + managers can upload a worker's selfie / photo ID on their behalf
  const canUploadDocs = userType === 'admin' || userType === 'manager';
  // All staff roles can message workers
  const canMessageUsers = ['admin', 'manager', 'auditor'].includes(userType);

  // Send message state
  const [userToMessage, setUserToMessage] = useState(null);
  const [messageTitle, setMessageTitle] = useState('');
  const [messageText, setMessageText] = useState('');
  const [sendEmailToo, setSendEmailToo] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  // Document upload state — `${userId}-${docType}` while uploading
  const [uploadingDoc, setUploadingDoc] = useState(null);
  // Message history state
  const [messageHistory, setMessageHistory] = useState({}); // userId -> messages[]
  const [historyOpen, setHistoryOpen] = useState({}); // userId -> bool
  const [loadingHistory, setLoadingHistory] = useState(null);

  const fetchMessageHistory = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/message-history`, getAuthHeader());
      setMessageHistory(prev => ({ ...prev, [userId]: res.data.messages }));
    } catch {
      toast.error('Failed to load message history');
    }
  };

  const toggleMessageHistory = async (user) => {
    const isOpen = !!historyOpen[user.id];
    setHistoryOpen(prev => ({ ...prev, [user.id]: !isOpen }));
    if (!isOpen && !messageHistory[user.id]) {
      setLoadingHistory(user.id);
      await fetchMessageHistory(user.id);
      setLoadingHistory(null);
    }
  };

  const openMessageModal = (user) => {
    setUserToMessage(user);
    setMessageTitle('');
    setMessageText('');
    setSendEmailToo(true);
  };

  const handleSendMessage = async () => {
    if (!userToMessage || !messageText.trim()) return;
    setSendingMessage(true);
    try {
      const res = await axios.post(`${API}/admin/users/${userToMessage.id}/send-message`, {
        title: messageTitle.trim() || null,
        message: messageText.trim(),
        send_email: sendEmailToo,
      }, getAuthHeader());
      toast.success(res.data.message);
      if (messageHistory[userToMessage.id]) fetchMessageHistory(userToMessage.id);
      setUserToMessage(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleUploadDoc = async (user, docType, fileObj) => {
    if (!fileObj) return;
    const key = `${user.id}-${docType}`;
    setUploadingDoc(key);
    try {
      const formData = new FormData();
      formData.append('doc_type', docType);
      formData.append('file', fileObj);
      const res = await axios.post(`${API}/admin/users/${user.id}/upload-document`, formData, getAuthHeader());
      toast.success(res.data.message);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingDoc(null);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [filter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API}/admin/users${params}`, getAuthHeader());
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/approve`, {}, getAuthHeader());
      toast.success('User approved successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleDeny = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/deny`, {}, getAuthHeader());
      toast.success('User denied');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to deny user');
    }
  };

  const handleTogglePro = async (userId, currentProStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/toggle-pro`, {
        is_pro: !currentProStatus
      }, getAuthHeader());
      toast.success(currentProStatus ? 'Pro status removed' : 'Pro status granted');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update Pro status');
    }
  };

  const handleRateUser = async (userId, rating) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/rate`, {
        rating: rating
      }, getAuthHeader());
      toast.success(`User rating updated to ${rating} stars`);
      setRatingUser(null);
      setPendingRating(0);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update rating');
    }
  };

  const openDocViewer = (url, type) => {
    setSelectedDoc(url);
    setDocType(type);
    setShowDocModal(true);
  };

  const handleDeleteUser = async (userId) => {
    setDeletingUser(userId);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, getAuthHeader());
      toast.success('User deleted successfully');
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUser(null);
    }
  };

  const confirmDelete = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const handleToggleDisable = async (user) => {
    const newDisabled = !user.disabled;
    try {
      await axios.put(`${API}/admin/users/${user.id}/disable`, { disabled: newDisabled }, getAuthHeader());
      toast.success(`${user.name} has been ${newDisabled ? 'disabled' : 'enabled'}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const openResetPassword = (user) => {
    setUserToResetPassword(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowResetPassword(true);
  };

  const handleResetPassword = async () => {
    if (!userToResetPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setResettingPassword(true);
    try {
      await axios.put(
        `${API}/admin/users/${userToResetPassword.id}/reset-password`,
        { new_password: newPassword },
        getAuthHeader()
      );
      toast.success(`Password reset successfully for ${userToResetPassword.name}`);
      setShowResetPassword(false);
      setUserToResetPassword(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(search.toLowerCase()) ||
    user.email.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700">Approved</Badge>;
      case 'denied':
        return <Badge className="bg-red-100 text-red-700">Denied</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const renderStarRating = (rating) => {
    if (rating === null || rating === undefined) {
      return <span className="text-gray-400 text-sm">Not rated</span>;
    }
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={14}
            className={star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
          />
        ))}
        <span className="text-sm text-gray-600 ml-1">({rating.toFixed(1)})</span>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'pending', 'approved', 'denied'].map((status) => (
            <Button
              key={status}
              variant={filter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(status)}
              className={filter === status ? 'bg-purple-600' : ''}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
        </div>
      ) : filteredUsers.length === 0 ? (
        <Card className="p-12 text-center border-0 shadow-sm">
          <Users size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No users found</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredUsers.map((user) => (
            <Card key={user.id} className="border-0 shadow-sm overflow-hidden">
              <div
                className="p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedUser(expandedUser === user.id ? null : user.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-lg font-bold text-purple-600">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{user.name}</h3>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(user.status)}
                    {user.disabled && <Badge className="bg-red-100 text-red-700">Disabled</Badge>}
                    {expandedUser === user.id ? (
                      <ChevronUp size={20} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedUser === user.id && (
                <div className="px-4 pb-4 border-t bg-gray-50">
                  {/* User Stats Row */}
                  <div className="flex flex-wrap items-center gap-6 py-3 border-b">
                    <div className="flex items-center gap-2">
                      <Star size={16} className="text-yellow-500" />
                      <span className="text-sm text-gray-600">Rating:</span>
                      {renderStarRating(user.star_rating)}
                      
                      {/* Rating Controls for Admin/Auditor */}
                      {canRateUsers && user.status === 'approved' && (
                        <div className="flex items-center gap-1 ml-2">
                          {ratingUser === user.id ? (
                            <div className="flex items-center gap-2 bg-white rounded-lg p-2 shadow-sm border">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                  key={star}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPendingRating(star);
                                  }}
                                  className="hover:scale-110 transition-transform"
                                  data-testid={`rate-star-${star}`}
                                >
                                  <Star
                                    size={20}
                                    className={star <= pendingRating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300 hover:text-yellow-400'}
                                  />
                                </button>
                              ))}
                              <Button
                                size="sm"
                                className="ml-2 h-7 bg-green-600 hover:bg-green-700"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (pendingRating > 0) {
                                    handleRateUser(user.id, pendingRating);
                                  }
                                }}
                                disabled={pendingRating === 0}
                                data-testid={`confirm-rating-${user.id}`}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRatingUser(null);
                                  setPendingRating(0);
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                setRatingUser(user.id);
                                setPendingRating(user.star_rating || 0);
                              }}
                              data-testid={`edit-rating-${user.id}`}
                            >
                              Edit Rating
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Briefcase size={16} className="text-purple-500" />
                      <span className="text-sm text-gray-600">Jobs Completed:</span>
                      <span className="font-semibold text-purple-600">{user.jobs_completed || 0}</span>
                    </div>
                    {/* Anti-cheat: far-from-door delivery violations */}
                    {(user.delivery_violations_total || 0) > 0 && (
                      <div className="flex items-center gap-1 text-red-600 text-sm font-semibold" data-testid={`violation-count-${user.id}`}>
                        <AlertCircle size={14} />
                        <span>{user.delivery_violations_total} flagged delivery mark{user.delivery_violations_total === 1 ? '' : 's'} (far from door)</span>
                      </div>
                    )}
                    {/* Rating requirement indicator */}
                    {user.star_rating !== null && user.star_rating < 4.5 && (
                      <div className="flex items-center gap-1 text-amber-600 text-sm">
                        <AlertCircle size={14} />
                        <span>Below Pro requirement (4.5★)</span>
                      </div>
                    )}
                  </div>

                  {/* Documents Row */}
                  <div className="flex flex-wrap gap-4 py-3 border-b">
                    <div className="flex-1 min-w-[140px]">
                      <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                        <Camera size={12} /> Selfie
                      </p>
                      {user.selfie_url ? (
                        <img
                          src={user.selfie_url}
                          alt="Selfie"
                          className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-80 border"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDocViewer(user.selfie_url, 'Selfie');
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '';
                            e.target.style.display = 'none';
                            e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                          }}
                        />
                      ) : null}
                      <div className={`w-20 h-20 bg-gray-200 rounded-lg items-center justify-center ${user.selfie_url ? 'hidden' : 'flex'}`}>
                        <Camera size={24} className="text-gray-400" />
                      </div>
                      {canUploadDocs && (
                        <label
                          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-purple-600 border border-purple-200 rounded-md px-2 py-1 cursor-pointer hover:bg-purple-50"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`upload-selfie-btn-${user.id}`}
                        >
                          {uploadingDoc === `${user.id}-selfie`
                            ? <Loader2 size={12} className="animate-spin" />
                            : <Upload size={12} />}
                          Upload Photo
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={uploadingDoc === `${user.id}-selfie`}
                            onChange={(e) => { handleUploadDoc(user, 'selfie', e.target.files?.[0]); e.target.value = ''; }}
                            data-testid={`upload-selfie-input-${user.id}`}
                          />
                        </label>
                      )}
                    </div>
                    <div className="flex-1 min-w-[140px]">
                      <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                        <IdCard size={12} /> ID Document
                      </p>
                      {user.id_document_url ? (
                        <img
                          src={user.id_document_url}
                          alt="ID Document"
                          className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-80 border"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDocViewer(user.id_document_url, 'ID Document');
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '';
                            e.target.style.display = 'none';
                            e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                          }}
                        />
                      ) : null}
                      <div className={`w-20 h-20 bg-gray-200 rounded-lg items-center justify-center ${user.id_document_url ? 'hidden' : 'flex'}`}>
                        <IdCard size={24} className="text-gray-400" />
                      </div>
                      {canUploadDocs && (
                        <label
                          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-purple-600 border border-purple-200 rounded-md px-2 py-1 cursor-pointer hover:bg-purple-50"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`upload-id-btn-${user.id}`}
                        >
                          {uploadingDoc === `${user.id}-id_document`
                            ? <Loader2 size={12} className="animate-spin" />
                            : <Upload size={12} />}
                          Upload Photo
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={uploadingDoc === `${user.id}-id_document`}
                            onChange={(e) => { handleUploadDoc(user, 'id_document', e.target.files?.[0]); e.target.value = ''; }}
                            data-testid={`upload-id-input-${user.id}`}
                          />
                        </label>
                      )}
                    </div>
                    {canMessageUsers && (
                      <div className="flex flex-col items-end gap-2 ml-auto">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-600 border-blue-300 hover:bg-blue-50"
                          onClick={(e) => { e.stopPropagation(); openMessageModal(user); }}
                          data-testid={`send-message-btn-${user.id}`}
                          title="Send this user an in-app notification (and optional email)"
                        >
                          <MessageSquare size={14} className="mr-1.5" />
                          Send Message
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-gray-500 hover:text-blue-600"
                          onClick={(e) => { e.stopPropagation(); toggleMessageHistory(user); }}
                          data-testid={`message-history-btn-${user.id}`}
                          title="Show past messages sent to this user"
                        >
                          <History size={14} className="mr-1.5" />
                          Message History
                          {historyOpen[user.id] ? <ChevronUp size={13} className="ml-1" /> : <ChevronDown size={13} className="ml-1" />}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Message History panel */}
                  {canMessageUsers && historyOpen[user.id] && (
                    <div className="py-3 border-b" data-testid={`message-history-panel-${user.id}`}>
                      <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                        <History size={12} /> Messages sent to {user.name}
                      </p>
                      {loadingHistory === user.id ? (
                        <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                          <Loader2 size={13} className="animate-spin" /> Loading message history…
                        </div>
                      ) : (messageHistory[user.id] || []).length === 0 ? (
                        <p className="text-xs text-gray-400 py-1">No messages have been sent to this user yet.</p>
                      ) : (
                        <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                          {messageHistory[user.id].map((m) => (
                            <div key={m.id} className="bg-gray-50 border border-gray-100 rounded-md px-3 py-2" data-testid={`message-history-item-${m.id}`}>
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-xs font-semibold text-gray-800 truncate">{m.title}</p>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${m.read ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                                  {m.read ? 'Read' : 'Unread'}
                                </span>
                              </div>
                              <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap">{m.message}</p>
                              <p className="text-[10px] text-gray-400 mt-1">
                                {m.from_admin_name} · {m.created_at ? new Date(m.created_at.endsWith('Z') ? m.created_at : m.created_at + 'Z').toLocaleString() : ''}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                    {/* Only show PayPal email to full admins */}
                    {isFullAdmin ? (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail size={16} className="text-gray-400" />
                        <span className="text-gray-600">PayPal: {user.paypal_email}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail size={16} className="text-gray-400" />
                        <span className="text-gray-400 italic">PayPal: Hidden</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <Phone size={16} className="text-gray-400" />
                      <span className="text-gray-600">{user.phone}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin size={16} className="text-gray-400" />
                      <span className="text-gray-600">{user.address}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar size={16} className="text-gray-400" />
                      <span className="text-gray-600">
                        Registered: {new Date(user.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {user.ssn_ein && (
                      <div className="text-sm">
                        <span className="text-gray-500">SSN/EIN: </span>
                        <span className="text-gray-600">***-**-{user.ssn_ein}</span>
                      </div>
                    )}
                  </div>

                  {user.status === 'pending' && (
                    <div className="flex gap-3 mt-4 pt-4 border-t">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(user.id);
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle size={18} className="mr-2" />
                        Approve
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeny(user.id);
                        }}
                      >
                        <XCircle size={18} className="mr-2" />
                        Deny
                      </Button>
                    </div>
                  )}

                  {/* Pro Status Toggle - Always visible for approved users */}
                  {user.status === 'approved' && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <Crown size={18} className={user.is_pro ? 'text-yellow-500' : 'text-gray-400'} />
                        <span className="text-sm font-medium text-gray-700">
                          Pro Status
                        </span>
                        {user.is_pro && (
                          <Badge className="bg-yellow-100 text-yellow-700">
                            PRO
                          </Badge>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTogglePro(user.id, user.is_pro);
                        }}
                        className={`relative w-14 h-7 rounded-full transition-colors ${
                          user.is_pro 
                            ? 'bg-yellow-500' 
                            : 'bg-gray-300'
                        }`}
                        data-testid={`pro-toggle-${user.id}`}
                      >
                        <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform ${
                          user.is_pro ? 'translate-x-8' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  )}

                  {/* Active Jobs Section */}
                  {user.active_jobs && user.active_jobs.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center gap-2 mb-3">
                        <MapPinned size={18} className="text-blue-500" />
                        <span className="text-sm font-medium text-gray-700">Active Jobs</span>
                        <Badge className="bg-blue-100 text-blue-700">{user.active_jobs.length}</Badge>
                      </div>
                      <div className="space-y-2">
                        {user.active_jobs.map((job) => (
                          <div
                            key={job.id}
                            className="flex items-center justify-between bg-blue-50 rounded-lg p-3"
                            data-testid={`active-job-${job.id}`}
                          >
                            <div>
                              <p className="font-medium text-gray-900">{job.title}</p>
                              <p className="text-sm text-gray-500">{job.client} • {job.deliveries} deliveries</p>
                            </div>
                            <a
                              href={job.tracking_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                              data-testid={`tracking-link-${job.id}`}
                            >
                              <MapPinned size={14} />
                              View Tracking
                              <ExternalLink size={12} />
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Jobs Section - Last 5 jobs worked on */}
                  {user.recent_jobs && user.recent_jobs.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center gap-2 mb-3">
                        <Briefcase size={18} className="text-emerald-500" />
                        <span className="text-sm font-medium text-gray-700">Recent Jobs</span>
                        <Badge className="bg-emerald-100 text-emerald-700">{user.recent_jobs.length}</Badge>
                      </div>
                      <div className="space-y-2">
                        {user.recent_jobs.map((job) => (
                          <div
                            key={job.id}
                            className="flex items-center justify-between bg-emerald-50 rounded-lg p-3"
                            data-testid={`recent-job-${job.id}`}
                          >
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900 truncate">
                                {job.job_number && <span className="text-emerald-600 mr-1">#{job.job_number}</span>}
                                {job.title}
                              </p>
                              <p className="text-sm text-gray-500">{job.client} &bull; {job.deliveries} deliveries &bull; <span className="capitalize">{job.status?.replace(/_/g, ' ')}</span></p>
                            </div>
                            <a
                              href={job.tracking_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1 text-emerald-600 hover:text-emerald-700 text-sm font-medium ml-3 shrink-0"
                              data-testid={`recent-tracking-link-${job.id}`}
                            >
                              <MapPinned size={14} />
                              Track
                              <ExternalLink size={12} />
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Admin Actions */}
                  {isFullAdmin && (
                    <div className="mt-4 pt-4 border-t flex gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleDisable(user);
                        }}
                        className={`flex-1 ${user.disabled ? 'text-green-600 border-green-200 hover:bg-green-50' : 'text-amber-600 border-amber-200 hover:bg-amber-50'}`}
                        data-testid={`toggle-disable-${user.id}`}
                      >
                        {user.disabled ? <ShieldCheck size={16} className="mr-2" /> : <ShieldOff size={16} className="mr-2" />}
                        {user.disabled ? 'Enable' : 'Disable'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openResetPassword(user);
                        }}
                        className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300"
                        data-testid={`reset-password-${user.id}`}
                      >
                        <Key size={16} className="mr-2" />
                        Reset Password
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmDelete(user);
                        }}
                        className="flex-1 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 size={16} className="mr-2" />
                        Delete User
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Document Viewer Modal */}
      <Dialog open={showDocModal} onOpenChange={setShowDocModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{docType}</DialogTitle>
          </DialogHeader>
          {selectedDoc && (
            <img src={selectedDoc} alt={docType} className="w-full h-auto max-h-[70vh] object-contain rounded-lg" />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle size={20} />
              Delete User
            </DialogTitle>
          </DialogHeader>
          {userToDelete && (
            <div className="space-y-4">
              <p className="text-gray-600">
                Are you sure you want to delete <strong>{userToDelete.name}</strong>? This action cannot be undone.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">
                  <strong>Warning:</strong> This will permanently remove the user's account, delivery history, and all associated data.
                </p>
              </div>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setUserToDelete(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteUser(userToDelete.id)}
                  disabled={deletingUser === userToDelete.id}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {deletingUser === userToDelete.id ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} className="mr-2" />
                      Delete User
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Message Modal */}
      <Dialog open={!!userToMessage} onOpenChange={(open) => { if (!open) setUserToMessage(null); }}>
        <DialogContent className="max-w-md" data-testid="send-message-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <MessageSquare size={20} />
              Send Message
            </DialogTitle>
          </DialogHeader>
          {userToMessage && (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                To <strong>{userToMessage.name}</strong> ({userToMessage.email}) — delivered as an in-app notification{sendEmailToo ? ' and email' : ''}.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title (optional)</label>
                <Input
                  placeholder="Message from Local Flyer Delivery"
                  value={messageTitle}
                  onChange={(e) => setMessageTitle(e.target.value)}
                  data-testid="message-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  className="w-full border rounded-md px-3 py-2 text-sm min-h-[110px] focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Type your message to this user…"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  data-testid="message-text-input"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={sendEmailToo}
                  onChange={(e) => setSendEmailToo(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  data-testid="message-send-email-checkbox"
                />
                Also send as an email
              </label>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setUserToMessage(null)} disabled={sendingMessage} data-testid="message-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={handleSendMessage}
                  disabled={sendingMessage || !messageText.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="message-send-btn"
                >
                  {sendingMessage ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} className="mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Key size={20} />
              Reset User Password
            </DialogTitle>
          </DialogHeader>
          {userToResetPassword && (
            <div className="space-y-4">
              <p className="text-gray-600">
                Reset password for <strong>{userToResetPassword.name}</strong> ({userToResetPassword.email})
              </p>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showPasswordText ? "text" : "password"}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordText(!showPasswordText)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="toggle-password-visibility"
                    >
                      {showPasswordText ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showPasswordText ? "text" : "password"}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordText(!showPasswordText)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="toggle-confirm-password-visibility"
                    >
                      {showPasswordText ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <p className="text-sm text-red-600">Passwords do not match</p>
              )}
              
              {newPassword && newPassword.length < 6 && (
                <p className="text-sm text-amber-600">Password must be at least 6 characters</p>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> The user will need to use this new password to log in. Consider notifying them via email or phone.
                </p>
              </div>

              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowResetPassword(false);
                    setUserToResetPassword(null);
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleResetPassword}
                  disabled={resettingPassword || newPassword.length < 6 || newPassword !== confirmPassword}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {resettingPassword ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      <Key size={16} className="mr-2" />
                      Reset Password
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
